import React, { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import { indexOfficialTechLogs, fetchOfficialTechLogsForBookings } from '../officialTechLog';
import { formatWingitDate, formatWingitTime } from '../dateFormat';
import {
  calculatePaymentTotals,
  isOutstandingPaymentBooking,
} from './paymentUtils';

export function formatPaymentCompletedAt(value) {
  const parsed = new Date(value || '');
  if (!Number.isFinite(parsed.getTime())) return '';

  return parsed.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).replace(',', ' at');
}

export default function AdminPaymentsPanel({ organisationId, isAdmin, accessToken, onBack }) {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const [showPaid, setShowPaid] = useState(false);
  const [actionState, setActionState] = useState({});
  const [actionError, setActionError] = useState('');
  const [refreshToken, setRefreshToken] = useState(0);

  useEffect(() => {
    let cancelled = false;

    async function loadPayments() {
      if (!isAdmin || !organisationId) {
        setRows([]);
        setStatus('ready');
        return;
      }

      setStatus('loading');
      setError('');

      const billingResponse = await fetch('/.netlify/functions/billing-settings-get', {
        headers: accessToken
          ? { Authorization: `Bearer ${accessToken}` }
          : {},
      });

      if (!billingResponse.ok) {
        if (!cancelled) {
          setError('Could not load organisation billing settings.');
          setStatus('error');
        }
        return;
      }

      const billing = await billingResponse.json();

      const { data: members, error: membersError } = await supabase
        .from('users')
        .select('id, auth_id, full_name, email, organisation_id')
        .eq('organisation_id', organisationId);

      if (membersError) {
        if (!cancelled) {
          setError('Could not load organisation members for payments.');
          setStatus('error');
        }
        return;
      }

      const memberIds = [...new Set(
        (members || [])
          .flatMap((member) => [member.id, member.auth_id])
          .map((id) => String(id || '').trim())
          .filter(Boolean)
      )];

      if (memberIds.length === 0) {
        if (!cancelled) {
          setRows([]);
          setStatus('ready');
        }
        return;
      }

      const { data: bookings, error: bookingsError } = await supabase
        .from('bookings')
        .select('id, user_id, aircraft_id, date, time, duration, end_date, end_time, tech_log_status, payment_status, payment_amount, payment_completed_at')
        .in('user_id', memberIds)
        .order('date', { ascending: false })
        .order('time', { ascending: false });

      if (bookingsError) {
        if (!cancelled) {
          setError('Could not load organisation payments.');
          setStatus('error');
        }
        return;
      }

      const bookingRows = bookings || [];
      const bookingIds = bookingRows.map((booking) => String(booking.id));
      const aircraftIds = [...new Set(bookingRows.map((booking) => booking.aircraft_id).filter(Boolean))];

      const [{ data: aircraft }, { data: techLogs }] = await Promise.all([
        aircraftIds.length > 0
          ? supabase
              .from('aircraft')
              .select('id, registration, icao_type')
              .eq('organisation_id', organisationId)
              .in('id', aircraftIds)
          : Promise.resolve({ data: [] }),
        bookingIds.length > 0
          ? fetchOfficialTechLogsForBookings(bookingIds)
          : Promise.resolve({ data: [] }),
      ]);

      const memberMap = (members || []).reduce((map, member) => {
        [member.id, member.auth_id].forEach((key) => {
          if (key) map[String(key)] = member;
        });
        return map;
      }, {});
      const aircraftMap = (aircraft || []).reduce((map, item) => {
        map[String(item.id)] = item;
        return map;
      }, {});
      const indexedLogs = indexOfficialTechLogs(techLogs || []);
      const visibleBookings = bookingRows
        .filter((booking) => {
          const techLog = indexedLogs.logMap[String(booking.id)] || {};
          return isOutstandingPaymentBooking(booking, techLog)
            || (showPaid && String(booking.payment_status || '').toLowerCase() === 'paid');
        })
        .map((booking) => ({
          ...booking,
          pilot: memberMap[String(booking.user_id)] || {},
          aircraft: aircraftMap[String(booking.aircraft_id)] || {},
          techLog: indexedLogs.logMap[String(booking.id)] || {},
          paymentTotals: calculatePaymentTotals({
            booking,
            techLogEntry: indexedLogs.logMap[String(booking.id)] || {},
            billingBasis: billing?.billingBasis,
            hourlyRate: billing?.hourlyRate,
            landingFeeEnabled: billing?.landingFeeEnabled,
            landingFee: billing?.landingFee,
          }),
        }));

      if (!cancelled) {
        setRows(visibleBookings);
        setStatus('ready');
      }
    }

    loadPayments();
    return () => {
      cancelled = true;
    };
  }, [accessToken, isAdmin, organisationId, refreshToken, showPaid]);

  async function updatePaymentStatus(bookingId, action) {
    const key = String(bookingId);
    setActionError('');
    setActionState((previous) => ({ ...previous, [key]: 'loading' }));

    try {
      const response = await fetch('/.netlify/functions/admin-payment-status-update', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
        body: JSON.stringify({ bookingId: key, action }),
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload?.error || 'Could not update payment status.');
      }

      setRefreshToken((value) => value + 1);
    } catch (updateError) {
      setActionError(updateError.message || 'Could not update payment status.');
    } finally {
      setActionState((previous) => {
        const next = { ...previous };
        delete next[key];
        return next;
      });
    }
  }

  if (!isAdmin) {
    return <div style={styles.message}>Only owners and admins can view payments.</div>;
  }

  return (
    <main style={styles.page}>
      <div style={styles.header}>
        <div>
          <div style={styles.kicker}>OPERATIONS</div>
          <h1 style={styles.title}>Payments</h1>
          <p style={styles.subtitle}>Outstanding payments for your organisation.</p>
        </div>
        <div style={styles.actions}>
          <button type="button" onClick={() => onBack('/admin')} style={styles.secondaryButton}>
            Back to Admin
          </button>
          <button type="button" onClick={() => onBack('/admin#payment-settings')} style={styles.secondaryButton}>
            Payment Settings
          </button>
          <button type="button" onClick={() => setShowPaid((value) => !value)} style={styles.secondaryButton}>
            {showPaid ? 'Hide Paid' : 'Show Paid'}
          </button>
        </div>
      </div>

      {actionError && <div style={styles.error}>{actionError}</div>}
      {status === 'loading' && <div style={styles.message}>Loading outstanding payments...</div>}
      {status === 'error' && <div style={styles.error}>{error}</div>}
      {status === 'ready' && rows.length === 0 && (
        <div style={styles.message}>No outstanding payments.</div>
      )}
      {status === 'ready' && rows.length > 0 && (
        <div style={styles.list}>
          {rows.map((row) => (
            <article key={row.id} style={styles.card}>
              <div>
                <div style={styles.primary}>{row.pilot?.full_name || row.pilot?.email || 'Unknown pilot'}</div>
                <div style={styles.secondary}>
                  {row.aircraft?.registration || 'Unknown aircraft'}
                  {row.aircraft?.icao_type ? ` / ${row.aircraft.icao_type}` : ''}
                </div>
                <div style={styles.meta}>
                  {formatWingitDate(row.date)} at {formatWingitTime(row.time)}
                </div>
              </div>
              <div style={styles.rightColumn}>
                <div style={styles.amount}>
                  {row.paymentTotals.amountAvailable
                    ? `£${row.paymentTotals.total.toFixed(2)}`
                    : 'Amount unavailable'}
                </div>
                <div style={{ ...styles.status, ...(row.payment_status === 'paid' ? styles.paidStatus : {}) }}>
                  {row.payment_status === 'paid' ? 'PAYMENT PAID' : 'PAYMENT OUTSTANDING'}
                </div>
                <div style={styles.meta}>
                  Tech Log: {String(row.tech_log_status || 'open').replace('_', ' ').toUpperCase()}
                </div>
                {row.payment_completed_at ? (
                  formatPaymentCompletedAt(row.payment_completed_at) ? (
                    <div style={styles.meta}>
                      Completed: {formatPaymentCompletedAt(row.payment_completed_at)}
                    </div>
                  ) : null
                ) : null}
                <button
                  type="button"
                  onClick={() => updatePaymentStatus(
                    row.id,
                    row.payment_status === 'paid' ? 'MARK_OUTSTANDING' : 'MARK_PAID'
                  )}
                  disabled={actionState[String(row.id)] === 'loading'}
                  style={styles.actionButton}
                >
                  {actionState[String(row.id)] === 'loading'
                    ? 'Saving...'
                    : row.payment_status === 'paid'
                      ? 'Mark Outstanding'
                      : 'Mark Paid'}
                </button>
              </div>
            </article>
          ))}
        </div>
      )}
    </main>
  );
}

const styles = {
  page: { maxWidth: 1100, margin: '2rem auto', padding: '0 1.25rem', color: '#d7ebff' },
  header: { display: 'flex', justifyContent: 'space-between', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '1.2rem' },
  kicker: { color: '#55c7ff', fontSize: '0.72rem', fontWeight: 900, letterSpacing: '0.14em' },
  title: { margin: '0.25rem 0 0', color: '#fff', fontSize: '2rem' },
  subtitle: { margin: '0.45rem 0 0', color: '#9fb7d7' },
  actions: { display: 'flex', gap: '0.6rem', flexWrap: 'wrap' },
  secondaryButton: { border: '1px solid rgba(182,234,255,0.22)', background: 'rgba(182,234,255,0.12)', color: '#d9f4ff', borderRadius: 8, padding: '0.6rem 0.85rem', fontWeight: 750, cursor: 'pointer' },
  message: { padding: '1rem', borderRadius: 12, background: '#23243a', border: '1px solid #2f466d', color: '#b6eaff' },
  error: { padding: '1rem', borderRadius: 12, background: '#3a1111', border: '1px solid #7d2c2c', color: '#ffb4b4' },
  list: { display: 'grid', gap: '0.7rem' },
  card: { display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap', padding: '1rem', borderRadius: 12, background: '#23243a', border: '1px solid #2f466d' },
  primary: { color: '#fff', fontWeight: 850, fontSize: '1rem' },
  secondary: { color: '#b6eaff', marginTop: '0.25rem' },
  meta: { color: '#8db7e6', fontSize: '0.82rem', marginTop: '0.3rem' },
  rightColumn: { textAlign: 'right' },
  amount: { color: '#fff', fontSize: '1.1rem', fontWeight: 850 },
  status: { color: '#ffcc66', fontSize: '0.72rem', fontWeight: 850, letterSpacing: '0.05em', marginTop: '0.25rem' },
  paidStatus: { color: '#7df0a8' },
  actionButton: { marginTop: '0.55rem', border: '1px solid rgba(255,204,102,0.35)', background: 'rgba(255,204,102,0.14)', color: '#ffdf9a', borderRadius: 7, padding: '0.38rem 0.6rem', fontWeight: 750, cursor: 'pointer' },
};
